/**
 * TaskPage — full task detail view.
 * Layout: timer compact in topbar, left details col, right comments col (narrower).
 *
 * Status + timer reactivity: the page passes useTask's `mutate` into the
 * timer hook, so every mutation (start, checkpoint, mark done) patches the
 * local task instantly — the status badge and timer never wait for a
 * refresh. Realtime UPDATE events then confirm with the authoritative row.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, isValid, parseISO } from 'date-fns';
import { useAuth } from '../../context/AuthContext.jsx';
import { useTask } from '../../hooks/usetasks.js';
import { useTaskTimer } from '../../hooks/Usetasktimer.js';
import { getDashboardRoute } from '../../utils/roleUtils.js';
import { TASK_STATUS, DOUBT_STATUS, PRIORITY_LABELS } from '../../data/constants.js';
import { formatSeconds, formatMinutes, secondsToMinutes } from '../../utils/timeUtils.js';
import { htmlToText } from '../../utils/sanitizeHtml.js';
import { supabase } from '../../api/supabase.js';

import AppShell from '../../components/layout/AppShell.jsx';
import CommentsThread from '../../components/task/Commentsthread.jsx';
import StatusBadge from '../../components/task/Statusbadge.jsx';
import RaiseDoubtModal from '../../components/task/Raisedoubtmodal.jsx';
import Avatar from '../../components/ui/Avatar.jsx';
import Spinner from '../../components/ui/Spinner.jsx';

const PRIORITY_COLORS = { low: '#94A3B8', medium: '#0891B2', high: '#DC2626' };

function fmtDate(d) {
  if (!d) return '—';
  const dt = typeof d === 'string' ? parseISO(d) : d;
  if (!isValid(dt)) return '—';
  return format(dt, 'd MMM yyyy');
}

function DetailRow({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-border-base last:border-b-0">
      <span className="text-xs uppercase tracking-wider text-muted font-semibold flex-shrink-0 w-24">
        {label}
      </span>
      <div className="text-sm text-ink text-right min-w-0">{children}</div>
    </div>
  );
}

function DoubtsSection({ taskId, isOwner, onRaiseDoubt, refreshKey }) {
  const [doubts, setDoubts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!taskId) return undefined;
    let mounted = true;

    async function load() {
      const { data, error: fetchError } = await supabase
        .from('doubts')
        .select('*, raiser:users!raised_by(id, full_name, avatar_url)')
        .eq('task_id', taskId)
        .order('created_at', { ascending: true });
      if (!mounted) return;
      if (fetchError) {
        console.error('[ORIN] Failed to fetch doubts:', fetchError.message);
        setLoadError(fetchError.message);
      } else {
        setLoadError(null);
        setDoubts(data || []);
      }
      setLoading(false);
    }

    load();

    const channel = supabase
      .channel(`doubts-task-${taskId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'doubts', filter: `task_id=eq.${taskId}` },
        () => { if (mounted) load(); },
      )
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, [taskId, refreshKey]);

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-muted">
          Doubts · {loading ? '…' : doubts.length}
        </span>
        {isOwner && (
          <button
            type="button"
            onClick={onRaiseDoubt}
            className="text-xs text-accent hover:underline font-medium"
          >
            + Raise a new doubt
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-4"><Spinner size={18} /></div>
      ) : loadError ? (
        <p className="text-xs text-status-overdue py-2">Couldn&apos;t load doubts: {loadError}</p>
      ) : doubts.length === 0 ? (
        <p className="text-xs text-muted py-2">No doubts raised for this task.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted uppercase tracking-wider">
                <th className="text-left pb-2 pr-3 font-semibold">Raised by</th>
                <th className="text-left pb-2 pr-3 font-semibold">Doubt</th>
                <th className="text-left pb-2 pr-3 font-semibold">Reply</th>
                <th className="text-left pb-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {doubts.map((d) => (
                <tr key={d.id} className="border-t border-border-base">
                  <td className="py-2 pr-3 align-top">
                    <span className="inline-flex items-center gap-1.5">
                      <Avatar name={d.raiser?.full_name} size="xs" />
                      <span className="font-medium text-ink">{d.raiser?.full_name || '—'}</span>
                    </span>
                  </td>
                  <td className="py-2 pr-3 align-top text-ink max-w-[160px]">{d.question}</td>
                  <td className="py-2 pr-3 align-top text-muted max-w-[160px]">
                    {d.response || <span className="italic">Waiting for Admin…</span>}
                  </td>
                  <td className="py-2 align-top">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold text-[10px]"
                      style={{
                        backgroundColor: d.status === DOUBT_STATUS.RESOLVED ? '#16A34A1A' : '#D974061A',
                        color: d.status === DOUBT_STATUS.RESOLVED ? '#16A34A' : '#D97706',
                      }}
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: d.status === DOUBT_STATUS.RESOLVED ? '#16A34A' : '#D97706' }}
                      />
                      {d.status === DOUBT_STATUS.RESOLVED ? 'Resolved' : 'Open'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function TaskPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { task, loading, error, mutate } = useTask(taskId);

  const isOwner = !!profile && !!task && profile.id === task.owner_id;
  const isDone = task?.status === TASK_STATUS.DONE;

  const { liveSeconds, isRunning, markDone } = useTaskTimer({
    task,
    isOwner,
    enabled: !isDone,
    onTaskPatch: mutate,
  });

  const [doubtOpen, setDoubtOpen] = useState(false);
  const [doubtRefresh, setDoubtRefresh] = useState(0);
  const [markingDone, setMarkingDone] = useState(false);

  const plainTitle = useMemo(() => htmlToText(task?.title) || 'Untitled task', [task?.title]);

  const handleBack = () => {
    navigate(profile?.role ? getDashboardRoute(profile.role) : -1);
  };

  const handleMarkDone = async () => {
    if (markingDone) return;
    setMarkingDone(true);
    try {
      await markDone();
    } finally {
      setMarkingDone(false);
    }
  };

  if (loading) {
    return (
      <AppShell title="Task" sectionLabel="Workspace">
        <div className="flex items-center justify-center py-24">
          <Spinner size={28} />
        </div>
      </AppShell>
    );
  }

  if (error || !task) {
    return (
      <AppShell title="Task" sectionLabel="Workspace">
        <div className="flex items-center justify-center py-24">
          <div className="card max-w-md w-full p-8 text-center">
            <h1 className="text-lg font-semibold mb-1">Task not found</h1>
            <p className="text-sm text-muted mb-5">
              {error || "This task doesn't exist or you don't have access to it."}
            </p>
            <button onClick={handleBack} className="btn">Back to dashboard</button>
          </div>
        </div>
      </AppShell>
    );
  }

  const priorityColor = PRIORITY_COLORS[task.priority] || '#6B7280';
  const actualMins = secondsToMinutes(liveSeconds);

  return (
    <AppShell title={plainTitle} sectionLabel="Workspace">

      {/* ── Top bar: back · status · timer · actions ── */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        {/* Left: back + event + status */}
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1 font-medium text-muted hover:text-ink transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="shrink-0">
              <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back
          </button>
          <span className="text-[var(--separator)]">·</span>
          <span className="text-muted font-medium">{task.event_name || 'No event'}</span>
          <span className="text-[var(--separator)]">·</span>
          <StatusBadge status={task.status} />
        </div>

        {/* Right: compact timer + actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Timer pill */}
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-mono font-semibold text-ink"
            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--canvas)' }}
            aria-live="off"
          >
            <span>{formatSeconds(liveSeconds)}</span>
            {!isDone && (
              <span
                className={`w-2 h-2 rounded-full ${isRunning ? 'animate-pulse' : ''}`}
                style={{ backgroundColor: isRunning ? 'var(--status-progress)' : '#94A3B8' }}
              />
            )}
          </div>

          {isOwner && !isDone && (
            <>
              <button
                type="button"
                onClick={handleMarkDone}
                disabled={markingDone}
                className="btn-primary disabled:opacity-60 text-sm px-4 py-1.5"
              >
                {markingDone ? 'Marking…' : 'Mark Done'}
              </button>
              <button
                type="button"
                onClick={() => setDoubtOpen(true)}
                className="btn text-sm px-3 py-1.5"
              >
                Raise Doubt
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Title + meta ── */}
      <h1 className="text-xl font-bold tracking-tight text-ink leading-snug mb-1">
        {plainTitle}
      </h1>
      <div className="flex items-center gap-2 flex-wrap mb-4 text-sm text-muted">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: priorityColor }} />
          {PRIORITY_LABELS[task.priority] || task.priority}
        </span>
        <span className="text-[var(--separator)]">·</span>
        <span>Due {fmtDate(task.closure_date)}</span>
        <span className="text-[var(--separator)]">·</span>
        <span className="inline-flex items-center gap-1.5">
          <Avatar name={task.owner?.full_name} photoUrl={task.owner?.avatar_url} size="xs" />
          {task.owner?.full_name || '—'}
          {isOwner && (
            <span
              className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
              style={{ backgroundColor: 'var(--accent-tint, #6366f11A)', color: 'var(--accent)' }}
            >
              You
            </span>
          )}
        </span>
      </div>

      {/* ── 2-column body: left wider (details+doubts), right narrower (comments) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 items-start">

        {/* LEFT — task details + doubts */}
        <div className="flex flex-col gap-4">

          {/* Details card */}
          <div className="card p-5">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-muted mb-1">
              Task Details
            </div>
            <DetailRow label="Owner">
              <span className="inline-flex items-center gap-2 justify-end">
                <Avatar name={task.owner?.full_name} photoUrl={task.owner?.avatar_url} size="sm" />
                <span className="font-medium">{task.owner?.full_name || '—'}</span>
              </span>
            </DetailRow>
            <DetailRow label="Event">{task.event_name || 'No event'}</DetailRow>
            <DetailRow label="Closure">{fmtDate(task.closure_date)}</DetailRow>
            <DetailRow label="Priority">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: priorityColor }} />
                {PRIORITY_LABELS[task.priority] || task.priority}
              </span>
            </DetailRow>
            <DetailRow label="Std Time">
              <span className="font-mono">{task.std_minutes}m</span>
              <span className="text-muted ml-1">±{task.variance_min}m</span>
            </DetailRow>
            <DetailRow label="Logged">
              <span className="font-mono">{formatMinutes(actualMins)}</span>
            </DetailRow>
            {task.creator && (
              <DetailRow label="Created by">{task.creator.full_name}</DetailRow>
            )}
          </div>

          {/* Doubts */}
          <DoubtsSection
            taskId={task.id}
            isOwner={isOwner}
            onRaiseDoubt={() => setDoubtOpen(true)}
            refreshKey={doubtRefresh}
          />
        </div>

        {/* RIGHT — comments (compact, sticky) */}
        <div className="lg:sticky lg:top-4">
          <CommentsThread taskId={task.id} />
        </div>
      </div>

      <RaiseDoubtModal
        open={doubtOpen}
        onClose={() => setDoubtOpen(false)}
        taskId={task.id}
        onRaised={() => setDoubtRefresh((k) => k + 1)}
      />
    </AppShell>
  );
}
